import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "../lib/api";
import LectureCard from "../components/LectureCard";
import { ArrowRight, Sparkles, BookOpen, Feather } from "lucide-react";

export default function Home() {
  const [data, setData] = useState(null);
  const [colls, setColls] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    (async () => {
      try {
        const [d, c] = await Promise.all([api.discover(), api.collections()]);
        setData(d); setColls(c.collections || []);
      } catch (e) { console.error(e); }
      finally { setLoading(false); }
    })();
  }, []);

  return (
    <div className="space-y-16 animate-fade-up" data-testid="page-home">
      {/* HERO */}
      <section className="relative overflow-hidden rounded-xl border border-white/5 bg-gradient-to-br from-[#1a1108] via-[#161009] to-[#0F0B08] px-5 sm:px-8 lg:px-12 py-10 sm:py-14 lg:py-20">
        <div className="absolute -top-20 -right-20 w-[420px] h-[420px] rounded-full bg-gold/[0.08] blur-3xl" />
        <div className="absolute bottom-0 left-0 w-[300px] h-[300px] rounded-full bg-emerald-glow/[0.08] blur-3xl" />
        <div className="relative max-w-2xl">
          <div className="flex items-center gap-2 mb-5">
            <Sparkles className="w-4 h-4 text-gold" />
            <span className="label-caps text-gold/90">Circles of Knowledge</span>
          </div>
          <h1 className="font-display text-3xl sm:text-4xl lg:text-6xl leading-[1.1] text-paper">
            Understanding Islam <br />
            <span className="italic text-gold">through the Salaf-as-Saleh.</span>
          </h1>
          <p className="mt-5 text-base sm:text-lg text-white/70 max-w-xl leading-relaxed">
            Majalisul Ilm is dedicated to the study of classical works of the righteous
            predecessors, transmitted by contemporary scholars grounded in the Sunnah — for
            those seeking correct manhaj and aqeedah.
          </p>
          <div className="mt-8 flex flex-wrap gap-3">
            <Link to="/explore" className="btn-primary inline-flex items-center gap-2" data-testid="hero-explore">
              Explore lectures <ArrowRight className="w-4 h-4" />
            </Link>
            <Link to="/collections" className="btn-ghost-gold inline-flex items-center gap-2" data-testid="hero-collections">
              Browse collections
            </Link>
          </div>
          <div className="mt-10 flex items-center gap-8">
            <Stat icon={<BookOpen className="w-4 h-4 text-emerald-soft" />} label="Lectures" value={data?.total?.toLocaleString?.() || "—"} />
            <div className="w-px h-8 bg-white/10" />
            <Stat icon={<Feather className="w-4 h-4 text-gold" />} label="Featured series" value={data?.featured_series?.length || 0} />
          </div>
        </div>
      </section>

      {/* Featured series */}
      <Section title="Featured Series" caption="Curated Study Threads" href="/collections">
        {loading && <SkeletonRow />}
        {!loading && (
          <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-3 gap-4">
            {(data?.featured_series || []).slice(0, 8).map(s => (
              <Link
                key={s.name}
                to={`/series/${encodeURIComponent(s.name)}`}
                data-testid={`series-card-${s.name}`}
                className="archival-card rounded-lg p-5 group"
              >
                <div className="flex items-center gap-4">
                  <div className="w-16 h-16 rounded-md overflow-hidden bg-black/40 border border-white/5 shrink-0">
                    <img src={s.thumbnail} alt="" className={`w-full h-full ${s.cover_fit === "contain" ? "object-contain" : "object-cover"}`} />
                  </div>
                  <div className="min-w-0 flex-1">
                    <p className="label-caps text-gold/90 mb-1 text-[10px]">{s.count} lectures</p>
                    <h4 className="font-display text-lg text-paper group-hover:text-emerald-soft truncate">{s.name}</h4>
                    <p className="text-xs text-white/50 mt-1 truncate">{s.creator}</p>
                  </div>
                </div>
              </Link>
            ))}
          </div>
        )}
      </Section>

      {/* Categories / Disciplines */}
      <Section title="Explore by Discipline" caption="Categories" href="/collections">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {colls.map((c) => (
            <Link
              key={c.slug}
              to={`/discipline/${c.slug}`}
              data-testid={`collection-${c.slug}`}
              className="archival-card featured rounded-lg p-6 relative overflow-hidden group"
            >
              <div className="absolute -right-8 -bottom-8 w-32 h-32 rounded-full blur-2xl opacity-40 bg-emerald-glow" />
              <span className="label-caps text-white/50">Discipline</span>
              <h4 className="font-display text-2xl text-paper mt-2 group-hover:text-emerald-soft transition-colors">{c.title}</h4>
              {c.translation && <p className="text-sm text-gold/80 italic mt-1">{c.translation}</p>}
              <p className="text-sm text-white/60 mt-3 leading-relaxed line-clamp-3">{c.description}</p>
              <div className="mt-4 inline-flex items-center gap-3 text-xs text-white/55">
                <span className="chip">{c.series_count} series</span>
                {c.live_series_count > 0 && <span className="chip active">{c.live_series_count} live</span>}
              </div>
              <div className="mt-4 inline-flex items-center gap-1 text-sm text-gold/90">
                Enter <ArrowRight className="w-3.5 h-3.5" />
              </div>
            </Link>
          ))}
        </div>
      </Section>

      {/* Popular */}
      <Section title="Most Listened" caption="Trending among students" href="/explore?sort=downloads+desc">
        {loading && <SkeletonRow />}
        {!loading && (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {(data?.popular || []).slice(0, 8).map(it => <LectureCard key={it.id} item={it} />)}
          </div>
        )}
      </Section>

      {/* Recent */}
      <Section title="Recently Added" caption="Fresh from the archive" href="/explore?sort=addeddate+desc">
        {loading && <SkeletonRow />}
        {!loading && (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {(data?.recent || []).slice(0, 6).map(it => <LectureCard key={it.id} item={it} />)}
          </div>
        )}
      </Section>
    </div>
  );
}

function Stat({ icon, label, value }) {
  return (
    <div>
      <div className="flex items-center gap-1.5 text-xs label-caps">{icon} {label}</div>
      <div className="font-display text-2xl text-paper mt-1">{value}</div>
    </div>
  );
}

function Section({ title, caption, href, children }) {
  return (
    <section>
      <div className="flex items-end justify-between mb-6">
        <div>
          <p className="label-caps text-gold/85">{caption}</p>
          <h2 className="font-display text-3xl text-paper mt-1">{title}</h2>
        </div>
        {href && (
          <Link to={href} className="text-sm text-white/60 hover:text-emerald-soft inline-flex items-center gap-1">
            View all <ArrowRight className="w-3.5 h-3.5" />
          </Link>
        )}
      </div>
      {children}
    </section>
  );
}

function SkeletonRow() {
  return (
    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
      {Array.from({ length: 4 }).map((_, i) => (
        <div key={i} className="archival-card rounded-lg p-4 flex gap-4 animate-pulse">
          <div className="w-[92px] h-[92px] bg-white/[0.03] rounded-md" />
          <div className="flex-1 space-y-3">
            <div className="h-3 w-24 bg-white/[0.04] rounded" />
            <div className="h-5 w-3/4 bg-white/[0.05] rounded" />
            <div className="h-3 w-1/2 bg-white/[0.03] rounded" />
          </div>
        </div>
      ))}
    </div>
  );
}
